import {Component} from '@angular/core';
import {QueryService} from '@pt/api-client';
import {AbstractWidgetDirective} from '../../abstract-widget.directive';
import {ActionsSubject, Store} from '@ngrx/store';
import {MIN_CHECKED_KEY, UiWidget, WidgetConfiguration, WidgetValidator} from '@pt/pt-models';
import {WidgetCheckboxV0Utils} from './widget-checkbox-v0.utils';
import {WidgetCheckboxV0NodeDto} from './models/widget-checkbox-v0-node.dto';
import {TranslateService} from '@ngx-translate/core';
import {WidgetDialogService} from '../../../services/widget-dialog.service';
import {NgxPermissionsService} from 'ngx-permissions';
import {WidgetCheckboxV0Mode} from './models/widget-checkbox-v0-mode';
import {WidgetUtils} from "../../widget.utils";
import {WidgetCheckboxV0ChildrenDto} from "./models/widget-checkbox-v0-children.dto";

@Component({
  selector: 'pt-widget-checkbox-v0',
  template: `
    <div class='position-relative' *ngIf="internalStatus.enabled">
      <pt-widget-disabled
        *ngIf='internalStatus.isDisabled'
        [showLoader]="internalStatus.viewMode === 'LOADING'">
      </pt-widget-disabled>

      <pt-widget-header
        class='w-100'
        [label]='widget?.label'
        [help]='widget?.help'

        [comment]="comment"
        [unsavedComment]="unsavedComment"
        [showCancelButton]='internalStatus.showCancelButton'
        [showCommentButton]='internalStatus.showCommentButton'
        [showEditButton]='internalStatus.showEditButton'
        [showReloadButton]='internalStatus.showReloadButton'
        [showSaveButton]='internalStatus.showSaveButton'
        [showOptButton]='internalStatus.showOptButton'

        [disabledCancelButton]='internalStatus.disabledCancelButton'
        [disabledCommentButton]='internalStatus.disabledCommentButton'
        [disabledEditButton]='internalStatus.disabledEditButton'
        [disabledOptButton]='internalStatus.disabledOptButton'
        [disabledReloadButton]='internalStatus.disabledReloadButton'
        [disabledSaveButton]='internalStatus.disabledSaveButton'
        [loading]='internalStatus.isLoading'

        (clickSaveWidget)='saveWidget()'
        (clickResetWidget)='resetWidget(widget)'
        (clickEditWidget)='dispatchEditState()'
        (clickReloadWidget)='reloadWidget(widget)'
        (clickCreateOpt)='createOptWidget()'
        (clickCommentWidget)='clickCommentWidget()'>
      </pt-widget-header>

      <ng-container *ngIf='!internalStatus.showOptButton'>

        <pt-widget-checkbox-v0-edit
          [noDataLabel]="noDataLabel"
          [flatViewMaxSize]="flatViewMaxSize"
          [internalId]="widget.internalId"
          [show]="internalStatus.viewMode === 'EDIT'"
          [hasChildren]="hasChildren"
          [childrenData]="childrenData"
          [mode]='mode'
          [checkbox]='editorData'
          [title]="widget?.label"
          (closeWithoutSave)="resetWidget(widget)"
          (changeAndSaveValue)="changeAndSaveValue($event)"
          (changeValue)='changeValue($event)'>
        </pt-widget-checkbox-v0-edit>


        <!-- Loading non può stare nell'editor perchè genera problemi con la modalità TREE-->
        <pt-widget-checkbox-v0-preview
          *ngIf="internalStatus.viewMode === 'PREVIEW' || internalStatus.viewMode === 'LOADING'"
          [noDataLabel]="noDataLabel"
          [mode]="mode"
          [hasChildren]="hasChildren"
          [childrenData]="childrenData"
          [previewData]='previewData'
          [loading]='internalStatus.isLoading'>
        </pt-widget-checkbox-v0-preview>

        <pt-widget-error
          *ngIf="internalStatus.viewMode === 'ERROR'"
          [errorMessage]='internalStatus.errorMessage | async'>
        </pt-widget-error>

        <pt-widget-footer>

        </pt-widget-footer>

      </ng-container>
    </div>

  `
})
export class WidgetCheckboxV0Component extends AbstractWidgetDirective<WidgetCheckboxV0NodeDto[]> {
  public editorData: WidgetCheckboxV0NodeDto[];
  public flat: boolean;
  public group: boolean;
  public hasChildren: boolean;
  // il suo valore è il risultato di un form che creo ogni volta che mi cambia il valore settando di conseguenza questa variabile
  formValid = false;

  // la funzione is valid viene utilizzata anche dalla classe astratta e dall'HTML.
  public mode: WidgetCheckboxV0Mode;
  private widgetCheckboxValue: string[];
  public childrenData: WidgetCheckboxV0ChildrenDto[];
  public flatViewMaxSize = 13;

  constructor(public store: Store,
              public actionsSubject: ActionsSubject,
              public translateService: TranslateService,
              public widgetDialogService: WidgetDialogService,
              public service: QueryService,
              public permissionService: NgxPermissionsService) {
    super(store, actionsSubject, translateService, widgetDialogService, permissionService);
  }

  loadWidget(widget: UiWidget): void {
    this.dispatchInitState();
    this.editorData = [];
    this.hasChildren = widget.properties.hasChildren || false;
    this.flatViewMaxSize = Number.isSafeInteger(widget?.properties?.flatViewMaxSize) ? widget?.properties?.flatViewMaxSize : 13;
    if (widget.properties.hasChildren) {
      /**
       * queryChildren": {
       *          "mapping": {
       *                     "normativeReference": "normativeReference",
       *                     "checkValue": "checkValue",
       *                     "idCheck": "idCheck",
       *                     "description": "description",
       *                     "help": "help",
       *                     "uuid": "uuid",
       *                     "parentField": "organizationalMeasureCategoryId"
       *                     "type": "typeLabel"
       *          },
       *          "table": "OrganizationalMeasureEntity"
       *        }
       */
      this.service.resolve(widget?.args?.queryChildren).subscribe(
        (queryResponse: unknown[]) => {
          const mapping = widget.args.queryChildren.mapping;
          this.childrenData = WidgetUtils.mappingQueryResponse(queryResponse, mapping) as WidgetCheckboxV0ChildrenDto[];
        }, () => {
          this.dispatchErrorState();
        }
      );
    }

    this.service.resolve(widget?.args?.query).subscribe(
      (queryResponse: unknown[]) => {
        const mapping = widget.args.query.mapping;
        this.widgetCheckboxValue = widget.value;
        this.editorData = WidgetCheckboxV0Utils.queryResponseToCheckBoxNodeWithValue(queryResponse, mapping, this.widgetCheckboxValue);
        this.mode = WidgetCheckboxV0Utils.getWidgetCheckboxV0Mode(widget?.properties?.flat, widget?.properties?.group, widget?.properties?.hasChildren);
        this.valueController(widget, this.editorData);
        this.checkValidity();
        this.dispatchCompleteState(this.editorData);
      }, () => {
        this.dispatchErrorState();
      }
    );
  }

  saveWidget(ignoreTrigger?: boolean): void {
    const widget = JSON.parse(JSON.stringify(this.widget)) as UiWidget;
    widget.value = this.widgetCheckboxValue;
    widget.comment = this.comment;
    this.completeSaveWidget(widget, ignoreTrigger);
  }

  changeAndSaveValue(value: string[]) {
    this.widgetCheckboxValue = value;
    this.checkValidity();
    this.saveWidget();
  }

  changeValue(value: string[]) {
    this.widgetCheckboxValue = value;
    this.checkValidity();
  }

  configuration(): WidgetConfiguration {
    return {supportAutoSave: false, supportValidation: false};
  }

  private checkValidity() {
    if (this.widget?.opt) {
      this.isValid$.next(true);
    } else {
      if (this.widget.validators) {
        const minCheckValidator = (this.widget.validators as WidgetValidator[]).find(v => v.key === MIN_CHECKED_KEY);
        if (minCheckValidator && minCheckValidator.args?.length === 1) {

          switch (this.internalStatus.viewMode) {
            case 'EDIT':
              this.isValid$.next(this.widgetCheckboxValue?.length >= minCheckValidator.args[0]);
              break;

            default:
              this.isValid$.next((this.widget.value as string[])?.length >= minCheckValidator.args[0]);
          }

        } else {
          this.isValid$.next(true);
        }
      } else {
        this.isValid$.next(true);
      }
    }
  }

  private valueController(widget: UiWidget, data: WidgetCheckboxV0NodeDto[]) {

    const noData = !data || data?.length === 0;
    if (noData) {
      this.sendWidgetNotificationInvalidQueryResponseNoData(data);
    }

    // const noCategory = false; // !noData && editorData.filter((d) => (d as any).label != null && (d as any).label != undefined)?.length > 0
    // if (noCategory) {
    //   this.sendWidgetNotificationInvalidQueryResponse(
    //     this.translateService.stream('WIDGET_NOTIFICATION.INVALID_QUERY_RESPONSE.MISSING_CATEGORY', data)
    //   );
    // }

    const widgetValue = widget?.value ? widget?.value : [];
    const validValues: string[] = noData ? [] : WidgetCheckboxV0Utils.getWidgetCheckboxV0NodeDtoUUID(data);
    const validValue = WidgetCheckboxV0Utils.getValidValue(widgetValue, validValues);
    if (validValue?.length !== widgetValue?.length) {
      const updatedWidget = JSON.parse(JSON.stringify(widget));
      updatedWidget.value = validValue;
      this.sendWidgetNotificationInvalidQueryResponseInvalidValue(updatedWidget);
    }

  }
}
